import React, { FC, useEffect, useState } from 'react'
import styles from './Sticky.module.scss'
import { Button } from 'components/Phantom/Button'
import cx from 'classnames'
import { useScrollThreshold } from 'components/_hooks/useScrollThreshold'
import { scrollLocked } from 'utils/jss'

export interface StickyProps {
	stickyName: string
	price: string
	strikePrice: string
	monthlyPrice: string
	months: number
	showFinancing: boolean
	discount: string
	discountLine?: string
	discountLineMobile?: string
	setHeight: (height: number) => void
	href?: string
}

export const Sticky: FC<StickyProps> = (props) => {
	const [stickyVisible, setStickyVisible] = useState(false)
	const headerWiped = useScrollThreshold()

	useEffect(() => {
		const header = document.querySelector('header')
		const isInApp = window.location.href.includes('utm_source=app')

		const resizeListener = () => {
			if (header && window.innerWidth >= 1024 && headerWiped) {
				header.style.transform = 'translateY(-100%)'
				header.style.transition = 'transform 0.3s ease-in-out'
			} else if (!scrollLocked) {
				header.style.transform = 'none'
				header.style.transition = 'transform 0.3s ease-in-out'
			}
		}

		if (!isInApp) {
			setStickyVisible(headerWiped)
			resizeListener()
		}

		window.addEventListener('resize', resizeListener)
		return () => {
			window.removeEventListener('resize', resizeListener)
		}
	}, [headerWiped])

	useEffect(() => {
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					setStickyVisible(false)
				} else if (window.scrollY > 100) {
					setStickyVisible(true)
				}
			})
		})

		const footer = document.getElementsByTagName('footer')
		for (const el of footer) {
			observer.observe(el)
		}

		return () => {
			observer.disconnect()
		}
	})

	useEffect(() => {
		if (stickyVisible) {
			document.body.style.setProperty('--shortcut_bump', '6.5rem')
		} else {
			document.body.style.setProperty('--shortcut_bump', '0px')
		}
	}, [stickyVisible])

	return (
		<div
			className={cx(styles.container, { [styles.container_visible]: stickyVisible })}
			id={'sticky-bar'}
			style={{
				visibility: stickyVisible ? 'visible' : 'hidden',
			}}
		>
			<div className={styles.inner}>
				<h2 className={styles.sticky_title_desktop}>{props.stickyName}</h2>
				<div className={styles.pricing_desktop}>
					<p className={styles.top_line}>
						<span aria-label="Current price">{props.price}</span> <s aria-label="Old price">{props.strikePrice}</s> {props.showFinancing && <span>or {props.monthlyPrice}/mo*</span>}
					</p>
					<p className={styles.bottom_line}>{props.discountLine ? props.discountLine : `Save ${props.discount}. Limited time offer.`}</p>
				</div>

				<div className={styles.pricing_mobile}>
					<h2 className={styles.sticky_title_mobile}>{props.stickyName}</h2>
					<p className={styles.top_line}>
						<span aria-label="Current price">{props.price}</span> <s aria-label="Old price">{props.strikePrice}</s>{' '}
						{props.showFinancing && <span className={styles.mobile_financing}> or {props.monthlyPrice}/mo*</span>}
						<span className={styles.discount_mobile}>{props.discountLineMobile ? props.discountLineMobile : `${props.discount} off`}</span>
					</p>
				</div>

				<Button.Dark
					key={`sticky-shop-now-mobile-${props.stickyName}-${stickyVisible}`}
					id={'sticky-shop-now-mobile'}
					href={props.href || '/product/pod-cover/'}
					className={styles.mobile_button}
				>
					Shop now
				</Button.Dark>

				<Button.Dark
					key={`sticky-shop-now-desktop-${props.stickyName}-${stickyVisible}`}
					id={'sticky-shop-now-desktop'}
					href={props.href || '/product/pod-cover/'}
					className={styles.desktop_button}
				>
					Shop now
				</Button.Dark>
			</div>
		</div>
	)
}
